import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchNurturingPlanVersions } from 'app/apiService/nurturingPlan/nurturingPlanSettingsDataService';
import type { NurturingPlanVersion } from 'app/pages/NurturingPlanGeneratorPage/types';

interface UseNurturingPlanVersionsReturn {
  versions: NurturingPlanVersion[];
  selectedVersionId: string;
  versionsLoading: boolean;
  versionsError: string | null;
  onVersionChange: (id: string) => void;
  refetchVersions: () => Promise<NurturingPlanVersion[]>;
}

export const useNurturingPlanVersions = (
  dealerId: string,
): UseNurturingPlanVersionsReturn => {
  const [versions, setVersions] = useState<NurturingPlanVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  const dealerIdRef = useRef(dealerId);
  dealerIdRef.current = dealerId;

  const loadVersions = useCallback(
    async (dealer: string): Promise<NurturingPlanVersion[]> => {
      setVersionsLoading(true);
      setVersionsError(null);
      try {
        const data = await fetchNurturingPlanVersions({ dealerId: dealer });
        setVersions(data);
        setSelectedVersionId(data.length > 0 ? data[0]._id : '');
        return data;
      } catch (err: any) {
        setVersionsError(err?.message || 'Failed to load versions');
        setVersions([]);
        setSelectedVersionId('');
        return [];
      } finally {
        setVersionsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!dealerId) {
      setVersions([]);
      setSelectedVersionId('');
      return;
    }
    loadVersions(dealerId);
  }, [dealerId, loadVersions]);

  const onVersionChange = useCallback((id: string) => {
    setSelectedVersionId(id);
  }, []);

  const refetchVersions = useCallback((): Promise<NurturingPlanVersion[]> => {
    const dealer = dealerIdRef.current;
    if (!dealer) return Promise.resolve([]);
    return loadVersions(dealer);
  }, [loadVersions]);

  return {
    versions,
    selectedVersionId,
    versionsLoading,
    versionsError,
    onVersionChange,
    refetchVersions,
  };
};
